import { AdProviderTypes } from '@/types'
import type { LoggerEvent } from '@/types/logger.types'

import { OutOfPageTypes } from './types'

const HISTORY_KEY = 'pgH5Ad'

type BatteryState = { readonly level: number; readonly charging: boolean }

const OPTIONAL_STRING_FIELDS = ['adUnit', 'adHbBidder', 'adHbRequestId', 'dimension1', 'dimension2'] as const

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// This tracker is loaded with AdService: keep history validation free of schema-library imports.
function parseMarker(value: unknown): { startedAt: number; event: LoggerEvent } | undefined {
    if (!isRecord(value) || typeof value.id !== 'string') {
        return
    }
    const { startedAt, event } = value
    if (typeof startedAt !== 'number' || !Number.isFinite(startedAt) || !isRecord(event)) {
        return
    }
    const placement = OutOfPageTypes.find((type) => type === event.adPlacement)
    const cpm = event.adHbCpmUsd
    if (
        event.event !== 'custom_event' ||
        event.eventName !== 'ad_h5_reload' ||
        event.adProvider !== AdProviderTypes.GAM_H5 ||
        !placement ||
        (event.waterfallIndex !== 1 && event.waterfallIndex !== 3) ||
        (cpm !== undefined && (typeof cpm !== 'number' || !Number.isFinite(cpm))) ||
        OPTIONAL_STRING_FIELDS.some((key) => event[key] !== undefined && typeof event[key] !== 'string')
    ) {
        return
    }
    const strings = Object.fromEntries(OPTIONAL_STRING_FIELDS.map((key) => [key, event[key]])) as Partial<
        Record<(typeof OPTIONAL_STRING_FIELDS)[number], string>
    >
    return {
        startedAt,
        event: {
            event: 'custom_event',
            eventName: 'ad_h5_reload',
            adProvider: AdProviderTypes.GAM_H5,
            adPlacement: placement,
            waterfallIndex: event.waterfallIndex,
            adHbCpmUsd: cpm,
            ...strings,
        },
    }
}

function writeMarker(marker?: { id: string; startedAt: number; event: LoggerEvent }) {
    const { [HISTORY_KEY]: _previous, ...rest } = window.history.state ?? {}
    window.history.replaceState(marker ? { ...rest, [HISTORY_KEY]: marker } : rest, '')
}

function clearMarker(id?: string) {
    const { state } = window.history
    if (!state?.[HISTORY_KEY] || (id && state[HISTORY_KEY].id !== id)) {
        return
    }
    writeMarker()
}

/** Consume once; use navigation start rather than the later ad-service initialization time. */
export async function logH5Reload(logEvent: (event: LoggerEvent) => void): Promise<void> {
    try {
        const marker = parseMarker(window.history.state?.[HISTORY_KEY])
        clearMarker()
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
        if (!marker || navigation?.type !== 'reload') {
            return
        }
        const elapsedMs = performance.timeOrigin - marker.startedAt
        if (elapsedMs >= 5000 && elapsedMs <= 30000) {
            let dimension1 = 'unavailable'
            try {
                const batteryNavigator = navigator as Navigator & { getBattery?: () => Promise<BatteryState> }
                if (batteryNavigator.getBattery) {
                    const battery = await batteryNavigator.getBattery()
                    dimension1 = `level=${Math.round(battery.level * 100)};charging=${battery.charging}`
                }
            } catch {
                // Send the event even if battery access is unsupported or blocked.
            }
            logEvent({ ...marker.event, dimension1, dimension3: `${elapsedMs / 1000}` })
        }
    } catch {
        // History access must never interrupt ad initialization.
    }
}

/** Only the matching ad may clear its marker if another ad has since started. */
export function trackH5Reload(event: LoggerEvent): () => void {
    const id = crypto.randomUUID()
    try {
        writeMarker({ id, startedAt: Date.now(), event })
    } catch {
        // Some embedding environments restrict History access.
    }
    return () => {
        try {
            clearMarker(id)
        } catch {
            // Cleanup is best effort, just like the initial marker write.
        }
    }
}
